import { WmButton } from "@watermarkinsights/ripple-react";
import { Fragment, useContext, useEffect, useReducer } from "react";
import { AppContext, AppContextInterface } from "@/contexts/AppContext";
import translate from "@/i18n/translate";
import { ResponseRateSection } from "./ces/ResponseRateSection";
import { ResponseRateFlyout } from "./ces/ResponseRateFlyout";
import { sendRequest } from "@/common/http";
import WidgetLoader from "@/features/common/elements/WidgetLoader";
import ApiError from "@/features/common/elements/ApiError";

export interface ResponseRateResult {
  courseEvaluations: number;
  responded: number;
  notResponded: number;
  respondedPercentage: number;
  notRespondedPercentage: number;
  total: number;
  optedOut: number;
  responseRateByCourseSection: ResponseRateByCourseSection[];
}

export interface ResponseRateByCourseSection {
  id: string;
  responded: number;
  notResponded: number;
  respondedPercentage: number;
  notRespondedPercentage: number;
  total: number;
  optedOut: number;
  daysLeft: number;
  courseSection: string;
  projectTitle: string;
}

interface State {
  responseRateOverview: ResponseRateResult | null;
  isDrilldownOpen: boolean;
  responseRateOverviewError: boolean;
  showLoader: boolean;
  CESHomePath: string;
}
interface Action {
  type: string;
  responseRateOverview: ResponseRateResult | null;
  isDrilldownOpen: boolean;
  CESHomePath: string;
}
const defaultAction: Action = {
  type: "",
  responseRateOverview: null,
  isDrilldownOpen: false,
  CESHomePath: ""
};

const initialState: State = {
  responseRateOverview: null,
  responseRateOverviewError: false,
  isDrilldownOpen: false,
  showLoader: true,
  CESHomePath: ""
};



const stateReducer = (state: State, action: Action) => {
  switch (action.type) {
    case "setResponseRateOverview":
      return {
        ...state,
        responseRateOverview: action.responseRateOverview,
        showLoader: false
      };
    case "setResponseRateOverviewError":
      return {
        ...state,
        responseRateOverviewError: true,
        showLoader: false
      };
    case "setCourseEvaluationsHomePath":
      return {
        ...state,
        CESHomePath: action.CESHomePath
      };
    case "ToggleDrilldown":
      return { ...state, isDrilldownOpen: !state.isDrilldownOpen };
    case "CloseDrilldown":
      return { ...state, isDrilldownOpen: false };
    default:
      return state;
  }
};

export function ResponseRateOverview() {
  const [state, dispatch] = useReducer(stateReducer, initialState);
  const { cesUserRole } = useContext<AppContextInterface>(AppContext);
  const setIsDrilldownOpen = () => {
    dispatch({ ...defaultAction, type: "ToggleDrilldown" });
  };

  const openDrilldownView = () => {
    setIsDrilldownOpen();
  };

  const closeDrilldownView = () => {
    dispatch({ ...defaultAction, type: "CloseDrilldown" });
  };

  useEffect(() => {
    const applyResult = (result: ResponseRateResult) => {
      dispatch({
        ...defaultAction,
        type: "setResponseRateOverview",
        responseRateOverview: result
      });
    };
    const onError = () => {
      dispatch({
        ...defaultAction,
        type: "setResponseRateOverviewError"
      });
    };
    sendRequest(
      { uri: "/ces/ce-response-rates", camelize: true },
      applyResult,
      onError
    );

    const applyPathResult = (result: string) => {
      dispatch({
        ...defaultAction,
        type: "setCourseEvaluationsHomePath",
        CESHomePath: result
      });
    };
    sendRequest(
      {
        uri: `/ces/results-home-path`,
        camelize: true
      },
      applyPathResult
    );
  }, []);

  return (
    <>
      {cesUserRole && cesUserRole.isInstructor && (
        <div className="response-rate-container">
          <div className="widget-card response-rate-overview-card paper-card">
            <div className="card-header header">
              <h3 className="response-rate-heading title">
                {translate("app.home.responseRateOverview.title")}
              </h3>
              <div className="base-font">
                {translate("app.home.responseRateOverview.desc")}
              </div>
            </div>
            <div className="body">
              {state.showLoader && (
                <WidgetLoader
                  loaderContainerClasses="loader"
                  loaderClasses="content"
                />
              )}

              {state.responseRateOverviewError && (
                <div className="widget-body">
                  <ApiError />
                </div>
              )}

              {!state.showLoader && !state.responseRateOverviewError && state.responseRateOverview && state.responseRateOverview.courseEvaluations >= 0 && (
                <ResponseRateOverviewBody
                  responseRateOverview={state.responseRateOverview}
                  openDrilldownView={openDrilldownView}
                  cesHomePath={state.CESHomePath}
                />
              )}
            </div>
            <div className="footer">
              {state.CESHomePath !== "" && (
                <div className="visitCesHome">
                  <a className="navigation-link" href={state.CESHomePath}>
                    {translate("app.ces.responseRate.cesResultsHome")}
                  </a>
                </div>
              )}
            </div>
          </div>

          <Fragment>
            <ResponseRateFlyout
              CloseDrilldownView={closeDrilldownView}
              responseRateByCourseSection={
                state?.responseRateOverview?.responseRateByCourseSection || []
              }
              open={state.isDrilldownOpen}>
            </ResponseRateFlyout>
          </Fragment>
        </div>
      )}
    </>
  );
}

interface ResponseRateOverviewBody {
  responseRateOverview: ResponseRateResult | null;
  openDrilldownView: () => void;
  cesHomePath: string;
}

function ResponseRateOverviewBody({
  responseRateOverview,
  openDrilldownView,
  cesHomePath
}: ResponseRateOverviewBody) {
  return (
    <>
      <div className="widget-body">
        <div className="overview">
          {translate("app.home.responseRateOverview.overview")}
        </div>
        <div className="responses">{responseRateOverview?.courseEvaluations}</div>
        <div className="course-evaluations-desc">
          {translate("app.home.responseRateOverview.courseEvaluationsDesc")}
        </div>
        {responseRateOverview && responseRateOverview.courseEvaluations > 0 && (
          <ResponseRateSection
            responseRateOverview={responseRateOverview}
            openDrilldownView={openDrilldownView}
          />
        )}
      </div>
    </>
  );
}
